import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import React, { useEffect } from 'react';

interface ModalProgressActivacionProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  taskStatus: any;
}


const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'SUCCESS' || status === 'SUCCESS_1' || status === 'SUCCESS_WITH_EMAIL_WARNING') {
    return <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />;
  }
  if (status === 'WARNING') {
    return <WarningAmberIcon sx={{ fontSize: 64, color: 'warning.main' }} />;
  }
  if (status === 'FAILURE') {
    return <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />;
  }
  return <CircularProgress size={64} />;
};

const getStatusMessage = (taskStatus:any): string => {
    if (!taskStatus) return 'Iniciando proceso...';

    switch (taskStatus.status) {
        case 'STARTED':
        return 'Iniciando activación de cliente...';
        case 'PROGRESS':
        return 'Vinculando cliente a OLT...';
        case 'SUCCESS':
        return 'Cliente activado correctamente';
        case 'SUCCESS_WITH_EMAIL_WARNING':
        return 'Cliente activado correctamente';
        case 'SUCCESS_1':
        return 'Cliente suspendido correctamente';
        case 'WARNING':
        return 'Cliente activado con advertencias';
        case 'FAILURE':
        return 'Error en el proceso ejecutado para el cliente';
        default:
        return 'Procesando...';
    }
};


const ModalProgressActivacion: React.FC<ModalProgressActivacionProps> = ({
    open,
    setOpen,
    taskStatus
}) => {

  const isCompleted = taskStatus?.status === 'SUCCESS'
    || taskStatus?.status === 'SUCCESS_1'
    || taskStatus?.status === 'SUCCESS_WITH_EMAIL_WARNING';
  const isEmailWarning = taskStatus?.status === 'SUCCESS_WITH_EMAIL_WARNING';
  const isWarning = taskStatus?.status === 'WARNING';
  const isFailed = taskStatus?.status === 'FAILURE';
  const isProcessing = !isCompleted && !isWarning && !isFailed;

  const statusMessage = getStatusMessage(taskStatus);

  // Efecto para manejar la finalización
  useEffect(() => {
    console.log({taskStatus})
    if (isCompleted) {
      const timer = setTimeout(() => {
        setOpen(false)
      }, 5000);
      return () => {
        clearTimeout(timer)
      };
    }
    if (isWarning || isFailed) {
      // No cerrar automáticamente en caso de error
    }
  }, [isCompleted, isWarning, isFailed]);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={taskStatus?.status === 'PROGRESS'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
        },
      }}
    >
      <Box sx={{ textAlign: 'center', pt: 2, pb: 1 }}>
        <Typography variant="h5" component="div" fontWeight="bold">
          {isCompleted ? 'Proceso de Aprovisionamiento Completado' : isWarning ? 'Proceso Completado con Advertencias' : isFailed ? 'Error en el Proceso' : 'Procesando aprovisionamiento de cliente'}
        </Typography>
      </Box>

      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={3}
          py={3}
        >
          {/* Icono de estado */}
          <StatusIcon status={taskStatus?.status || 'PENDING'} />

          {/* Mensaje de estado */}
          <Typography
            variant="body1"
            textAlign="center"
            color={isFailed ? 'error.main' : isWarning ? 'warning.main' : 'text.primary'}
          >
            {statusMessage}
          </Typography>

          {/* Información adicional en éxito */}
          {isCompleted && (
            <>
              <Alert severity="success" sx={{ width: '100%' }}>
                <Typography variant="body2">
                  {taskStatus?.message || 'Proceso de aprovisionamiento completado exitosamente'}
                </Typography>
              </Alert>
              {isEmailWarning && (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {taskStatus?.secondaryMessage || 'No se pudo enviar el correo de bienvenida.'}
                </Typography>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpen(false)}
              >
                Cerrar
              </Button>
            </>
          )}

          {isWarning && (
            <>
              <Alert severity="warning" sx={{ width: '100%' }}>
                <Typography variant="body2">
                  {taskStatus?.message || 'El cliente fue activado, pero una sincronizacion secundaria quedo pendiente.'}
                </Typography>
              </Alert>
              <Button
                variant="contained"
                color="warning"
                onClick={() => setOpen(false)}
              >
                Cerrar
              </Button>
            </>
          )}

          {/* Información de error */}
          {isFailed && (
            <>
              <Alert severity="error" sx={{ width: '100%' }}>
                <Typography variant="body2">
                  {taskStatus?.message || 'Ocurrió un error durante el proceso. Por favor intente nuevamente.'}
                </Typography>
              </Alert>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpen(false)}
              >
                Cerrar
              </Button>
            </>
          )}

          {/* Advertencia */}
          {isProcessing && (
            <Alert severity="warning" sx={{ width: '100%' }}>
              <Typography variant="body2">
                Por favor no cierre esta ventana hasta que el proceso termine.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ModalProgressActivacion;
